import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ButtonVariant, DropdownOption, DropdownOptionGroup, DropdownOptionIcon, DropdownOptionType, DropdownSelectionType, IconName, PopoverPosition } from '@yupma/ui-kit';
import { ActivityField, EntityAddFormField, EntityType, FieldDocument, FieldGroupDocument, FieldType } from 'server-model';
import { CRMFieldDataService, IAMService } from 'src/app/services/services.module';
import { FieldTypeIconMap } from 'src/app/utils';

@Component({
  selector: 'app-field-selection-dropdown',
  templateUrl: './field-selection-dropdown.component.html',
  styleUrl: './field-selection-dropdown.component.scss'
})
export class FieldSelectionDropdownComponent implements OnInit, OnChanges {
  @Input() label: string = 'Add field';
  @Input() value: EntityAddFormField[] = []
  @Input() allFields: FieldDocument[] | ActivityField[] = []
  @Input() allGroups: FieldGroupDocument[] = []
  @Input() selectedFields: string[] = []
  @Input() entityType: EntityType = EntityType.CONTACT;
  @Input() hideReadOnlyValueField: boolean = false;
  @Input() canMultiSelect: boolean = true;
  @Input() fullWidth: boolean = false;
  @Output() valueChange = new EventEmitter<EntityAddFormField[]>();

  buttonVariants = ButtonVariant;
  dropdownOptions: DropdownOptionGroup[] = [];
  filteredDropdownOptions: DropdownOptionGroup[] = [];
  isDropdownOpen: boolean = false;
  popoverPositions = PopoverPosition;
  dropdownSelectionTypes = DropdownSelectionType;

  // Add this to track search state
  private isSearching: boolean = false;
  private lastSearchQuery: string = '';

  constructor(
    private iamService: IAMService,
    private crmFieldDataService: CRMFieldDataService,
  ) { }

  ngOnInit() {
    this.createDropdown();
    this.setSelectedOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['value'] || changes['allFields'] || changes['allGroups'] || changes['selectedFields'] || changes['canMultiSelect']) {
      this.createDropdown();
      this.setSelectedOptions();
    }
  }

  async toggleDropdown(state: boolean) {
    this.isDropdownOpen = state;
    if (state) {
      // Reset search state and filtered options when opening dropdown
      this.isSearching = false;
      this.lastSearchQuery = '';
      this.filteredDropdownOptions = [...this.dropdownOptions];
    }
  }

  private createDropdown() {
    if (!this.allFields) return;

    // Check if we're dealing with ActivityField[] or FieldDocument[]
    const isActivityFields = this.allFields.length > 0 && !('group' in this.allFields[0]);

    if (isActivityFields) {
      // Handle ActivityField[] - create a single group since ActivityField doesn't have groups
      const activityFields = this.allFields as ActivityField[];
      const filteredFields = activityFields
        .filter(field => 
          !this.hideReadOnlyValueField || !field.is_readonly_value
        )
        .sort((a, b) => a.label.localeCompare(b.label));

      const options = filteredFields.map(field => {
        const iconName = FieldTypeIconMap[field.field_type || FieldType.SINGLE_LINE_TEXT];
        const isSelected = this.selectedFields.includes(field.name);

        return new DropdownOptionIcon(
          DropdownOptionType.DEFAULT,
          field.label,
          field.name,
          iconName,
          isSelected
        );
      });

      // Create a single group for all activity fields
      this.dropdownOptions = options.length > 0 ? [new DropdownOptionGroup('Activity Fields', options)] : [];
    } else {
      // Handle FieldDocument[] - use groups as before
      if (!this.allGroups) return;

      const fieldDocuments = this.allFields as FieldDocument[];
      this.dropdownOptions = this.allGroups
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(group => {
          const fieldsInGroup = fieldDocuments
            .filter(field =>
              field.group === group.name &&
              !field.is_archived &&
              (!this.hideReadOnlyValueField || !field.is_readonly_value)
            )
            .sort((a, b) => a.label.localeCompare(b.label));

          const options = fieldsInGroup.map(field => {
            const iconName = FieldTypeIconMap[field.field_type || FieldType.SINGLE_LINE_TEXT];
            const isSelected = this.selectedFields.includes(field.name);

            return new DropdownOptionIcon(
              DropdownOptionType.DEFAULT,
              field.label,
              field.name,
              iconName,
              isSelected
            );
          });

          return new DropdownOptionGroup(group.label, options);
        })
        .filter(group => group.options.length > 0); // Remove empty groups
    }

    // If not currently searching, update filtered options
    if (!this.isSearching) {
      this.filteredDropdownOptions = [...this.dropdownOptions];
    } else {
      // Re-apply the last search if we were searching
      this.search(this.lastSearchQuery);
    }
  }

  selectedOption(selectedOption: DropdownOption) {
    if (!selectedOption) return;

    if (this.canMultiSelect) {
      // Multi-select behavior
      selectedOption.selected = !selectedOption.selected;

      if (selectedOption.selected) {
        const newEntityField: EntityAddFormField = {
          position: this.value.length,
          field_name: selectedOption.value,
          required: false,
          is_editable: true,
        };
        this.value = [...this.value, newEntityField];
      } else {
        this.value = this.value.filter(entityField =>
          entityField.field_name !== selectedOption.value
        );
      }
    } else {
      // Single-select behavior
      const isCurrentlySelected = selectedOption.selected;

      // If trying to deselect the currently selected option, do nothing
      if (isCurrentlySelected) {
        return;
      }

      // Clear all previous selections
      this.clearAllSelections();

      // Select the new option
      selectedOption.selected = true;
      const newEntityField: EntityAddFormField = {
        position: 0, // Always position 0 for single select
        field_name: selectedOption.value,
        required: false,
        is_editable: true,
      };
      this.value = [newEntityField]; // Always contains only one item
    }

    this.valueChange.emit([...this.value]);
    this.setSelectedOptions(); // Update all options' states
  }

  private clearAllSelections() {
    // Clear selections in dropdown options
    this.dropdownOptions.forEach(group => {
      group.options.forEach(option => {
        option.selected = false;
      });
    });

    // Clear selections in filtered options
    this.filteredDropdownOptions.forEach(group => {
      group.options.forEach(option => {
        option.selected = false;
      });
    });

    // Clear the value array
    this.value = [];
  }

  search(query: string) {
    this.lastSearchQuery = query;
    this.isSearching = !!query;

    if (!query) {
      this.filteredDropdownOptions = [...this.dropdownOptions];
      // Make sure to sync selection states when clearing search
      this.setSelectedOptions();
      return;
    }

    const lowerQuery = query.toLowerCase().trim();
    this.filteredDropdownOptions = this.dropdownOptions
      .map(group => {
        const filteredOptions = group.options.filter(option =>
          option.label.toLowerCase().includes(lowerQuery)
        );
        return new DropdownOptionGroup(group.text, filteredOptions);
      })
      .filter(group => group.options.length > 0);

    // Ensure selection states are preserved in filtered results
    this.setSelectedOptions();
  }

  private setSelectedOptions() {
    // Update main dropdown options
    this.dropdownOptions.forEach(group => {
      group.options.forEach(option => {
        // Use selectedFields array to determine selection state
        option.selected = this.selectedFields.includes(option.value);

        // Check if field is editable from value array
        const matchingEntityField = this.value.find(
          entityField => entityField.field_name === option.value
        );
        option.disabled = matchingEntityField ? !matchingEntityField.is_editable : false;
      });
    });

    // Also update filtered options if they exist
    if (this.filteredDropdownOptions && this.filteredDropdownOptions.length > 0) {
      this.filteredDropdownOptions.forEach(group => {
        group.options.forEach(option => {
          // Directly use selectedFields array for consistency
          option.selected = this.selectedFields.includes(option.value);

          // Check if field is editable from value array
          const matchingEntityField = this.value.find(
            entityField => entityField.field_name === option.value
          );
          option.disabled = matchingEntityField ? !matchingEntityField.is_editable : false;
        });
      });
    }
  }

  private findOptionInDropdownOptions(value: string): DropdownOption | null {
    for (const group of this.dropdownOptions) {
      const option = group.options.find(opt => opt.value === value);
      if (option) return option;
    }
    return null;
  }
}